import { useEffect, useState } from 'react'
import { getBuyerBeneficios, type BuyerBeneficios } from '@/lib/api-shell'

export type BuyerBeneficiosStatus = 'idle' | 'loading' | 'ready' | 'error'

/**
 * Fetches and caches buyer `beneficios` for the current authenticated session.
 */
export function useBuyerBeneficios(
  enabled: boolean = true,
  scopeKey: string | number | null = null
): {
  beneficios: BuyerBeneficios | null
  status: BuyerBeneficiosStatus
  revision: number
} {
  const [beneficios, setBeneficios] = useState<BuyerBeneficios | null>(null)
  const [status, setStatus] = useState<BuyerBeneficiosStatus>(enabled ? 'loading' : 'idle')
  const [revision, setRevision] = useState(0)
  const [loadedScopeKey, setLoadedScopeKey] = useState<string | number | null>(null)

  useEffect(() => {
    if (!enabled) {
      setBeneficios(null)
      setStatus('idle')
      setRevision(0)
      setLoadedScopeKey(null)
      return
    }

    let active = true
    let requestId = 0
    setBeneficios(null)
    setStatus('loading')
    setLoadedScopeKey(null)

    const load = (initial: boolean = false) => {
      const currentRequestId = ++requestId
      if (initial) setStatus('loading')

      void getBuyerBeneficios()
        .then((data) => {
          if (!active || currentRequestId !== requestId) return
          setBeneficios(data)
          setStatus('ready')
          setLoadedScopeKey(scopeKey)
          setRevision((current) => current + 1)
        })
        .catch(() => {
          if (!active || currentRequestId !== requestId) return
          setBeneficios(null)
          setStatus('error')
          setLoadedScopeKey(scopeKey)
        })
    }

    const handleFocus = () => load()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') load()
    }

    load(true)
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      active = false
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [enabled, scopeKey])

  const scopeMatches = loadedScopeKey === scopeKey
  return {
    beneficios: scopeMatches ? beneficios : null,
    status: enabled && !scopeMatches ? 'loading' : status,
    revision,
  }
}
